<template>
    <section class="appointment">
        <common-page-header
            :heading="$t('pages.referrals-pool.index.external_propose_time_page.heading')"
            size="small"
            contentSpacing="medium"
        ></common-page-header>
        <header class="appointment__header">
            <div class="appointment__months-input">
                <select-input
                    :value="{ year, month }"
                    :options="monthOptions"
                    @change="changeCurrentMonth($event)"
                ></select-input>
            </div>

            <div class="appointment__months-switch">
                <schedule-months-switch
                    :value="{ year, month }"
                    :options="monthOptions"
                    size="large"
                    @change="changeCurrentMonth($event)"
                ></schedule-months-switch>
            </div>
        </header>

        <div class="appointment__calendar">
            <external-calendar
                ref="externalCalendar"
                :year="year"
                :month="month"
                :dates="dates"
                :physician="physician"
                :practice="practice"
                :selectedSlotsByDate="selectedSlotsByDate"
                @emitEvent="emitEvent($event)"
            ></external-calendar>
        </div>
    </section>
</template>

<script>
    import { importComponents } from '~/helpers'
    import dayjs from 'dayjs'
    import SelectInput from 'components/form-inputs/SelectInput'
    import ScheduleDatesToCalendarDataTransformer from 'transformers/ScheduleDatesToCalendarDataTransformer'
    import ExternalCalendar from 'components/page-elements/referrals-new-schedule/ExternalCalendar'
    import { getEligibleDateRange, isDateEligible } from '@/utils/referralTimeframeUtils'

    export default {
        components: {
            ...importComponents(require.context('components/page-elements/referrals-new-schedule')),
            SelectInput,
            ExternalCalendar,
        },

        props: {
            physician: { type: Object, required: true },
            practice: { type: Object, required: true },
            selectedSlotsByDate: { type: Object, required: false, default: undefined },
            timeframe: { type: String, default: null },
            createdAt: { type: String, default: null },
        },

        inject: {
            isReferralPool: {
                default: false,
            },
        },

        provide() {
            return {
                referralTimeframe: this.timeframe,
                referralCreatedAt: this.createdAt,
            }
        },

        data() {
            return {
                dates: {},
                year: dayjs().year(),
                month: dayjs().month(),
            }
        },

        computed: {
            monthOptions() {
                return Array(12)
                    .fill(dayjs())
                    .map((month, index) => ({
                        label: month.add(index, 'months').format('MMMM YYYY'),
                        value: {
                            year: month.add(index, 'months').year(),
                            month: month.add(index, 'months').month(),
                        },
                    }))
            },
        },

        methods: {
            setInitialMonth() {
                if (!this.timeframe || !this.createdAt) {
                    return
                }

                const created = dayjs(this.createdAt)

                if (!created.isValid()) {
                    return
                }

                const eligibleRange = getEligibleDateRange(this.timeframe, created)
                let focusDate = eligibleRange.start.startOf('day')
                const today = dayjs().startOf('day')

                if (focusDate.isBefore(today)) {
                    focusDate = today
                }

                if (focusDate.isAfter(eligibleRange.end, 'day')) {
                    return
                }

                this.year = focusDate.year()
                this.month = focusDate.month()
            },

            changeCurrentMonth({ year, month }) {
                this.year = year
                this.month = month
                this.generateAvailableDates()
            },

            generateAvailableDates() {
                const dates = []
                const startOfMonth = dayjs().year(this.year).month(this.month).startOf('month')
                const endOfMonth = startOfMonth.endOf('month')
                const today = dayjs().startOf('day')
                const created = this.createdAt ? dayjs(this.createdAt) : null
                const hasTimeframeFilter = this.timeframe && created?.isValid()

                let rangeStart = today.add(1, 'day')
                let rangeEnd = endOfMonth

                if (hasTimeframeFilter) {
                    const eligibleRange = getEligibleDateRange(this.timeframe, created)
                    rangeStart = eligibleRange.start.startOf('day')
                    rangeEnd = eligibleRange.end

                    if (rangeStart.isBefore(today)) {
                        rangeStart = today
                    }
                }

                let currentDate = startOfMonth.isAfter(rangeStart) ? startOfMonth : rangeStart

                while (currentDate.isBefore(endOfMonth) || currentDate.isSame(endOfMonth, 'day')) {
                    if (currentDate.isAfter(rangeEnd, 'day')) {
                        break
                    }

                    const dayOfWeek = currentDate.format('dddd').toLowerCase()

                    if (
                        this.physician.workingHours?.[dayOfWeek]?.isWorking &&
                        (!hasTimeframeFilter || isDateEligible(currentDate, this.timeframe, created))
                    ) {
                        dates.push({
                            date: currentDate.format('YYYY-MM-DD'),
                            slotCount: 9,
                        })
                    }

                    currentDate = currentDate.add(1, 'day')
                }

                this.dates = ScheduleDatesToCalendarDataTransformer.transform(JSON.stringify(dates))
            },
        },

        mounted() {
            this.setInitialMonth()
            this.generateAvailableDates()
        },
    }
</script>
